import { DollarOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Popover, Progress, Tabs, Tooltip } from "antd";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useState } from "react";
import { BALANCE_BUCKETS as BUCKETS, CIRCLE_RADIUS } from "utils/balanceBuckets.ts";
import { formatCurrency } from "utils/formatCurrency.ts";

import { TenantLink } from "../TenantLink/TenantLink";

import BalanceWalletRoleData = App.Data.BalanceWalletRoleData;

interface HeaderWalletProps {
    roles: BalanceWalletRoleData[];
}

export const HeaderWallet = ({ roles }: HeaderWalletProps) => {
    const { t } = usePaldockTranslation("components.header-wallet");
    const [activeRoleKey, setActiveRoleKey] = useState<string>(roles[0]?.role ?? "");

    const activeRole = roles.find((role) => role.role === activeRoleKey) ?? roles[0];

    if (!activeRole) {
        return null;
    }

    const format = (amount: number) => formatCurrency(amount, activeRole.currency.currency_code);
    const total = activeRole.settled + activeRole.available + activeRole.approved + activeRole.pending;

    // Cumulative percent per bucket (in BUCKETS order). Stacking a Progress bar per bucket,
    // each filled from 0 up to its own cumulative percent, and layering them back-to-front
    // (largest cumulative first, i.e. reverse BUCKETS order) reveals each bucket's true
    // color only in its own slice — antd's Progress has no native "N differently-sized
    // segments" mode, so this is how we get proportional segments out of it.
    const cumulativePercents = BUCKETS.reduce<number[]>((acc, bucket) => {
        const previous = acc[acc.length - 1] ?? 0;
        const share = total > 0 ? (activeRole[bucket.key] / total) * 100 : 0;
        acc.push(Math.min(previous + share, 100));
        return acc;
    }, []);

    return (
        <Popover
            placement="bottomRight"
            trigger="click"
            content={
                <div className="flex flex-col min-w-[280px]">
                    {roles.length > 1 && (
                        <Tabs
                            activeKey={activeRoleKey}
                            onChange={setActiveRoleKey}
                            items={roles.map((role) => ({
                                key: role.role,
                                label: t(`role_${role.role}`),
                            }))}
                        />
                    )}
                    <span className="text-lg font-semibold mt-3">{t("title")}</span>
                    <Tooltip title={BUCKETS.map(({ key }) => `${t(key)}: ${format(activeRole[key])}`).join(" / ")}>
                        <div className="relative mt-3" style={{ height: 8 }}>
                            {BUCKETS.map(({ key, color }, index) => (
                                <Progress
                                    key={key}
                                    percent={cumulativePercents[index]}
                                    showInfo={false}
                                    strokeColor={color}
                                    trailColor="transparent"
                                    size={["100%", 8]}
                                    className="absolute inset-0"
                                />
                            )).reverse()}
                        </div>
                    </Tooltip>
                    <div className="flex flex-col gap-2 mt-4">
                        {BUCKETS.map(({ key, color, textColor, bold, tooltip }) => (
                            <div key={key} className="flex items-center gap-2">
                                <div
                                    style={{ width: 16, height: 16, backgroundColor: color, ...CIRCLE_RADIUS }}
                                    className="flex-shrink-0"
                                />
                                <span className="text-lg" style={{ color: textColor }}>
                                    {t(key)}
                                </span>
                                {tooltip && (
                                    <Tooltip title={t(`${key}_tooltip`)}>
                                        <QuestionCircleOutlined style={{ fontSize: 10, color: textColor }} />
                                    </Tooltip>
                                )}
                                <span
                                    className={`text-lg ml-auto ${bold ? "font-semibold" : ""}`}
                                    style={{ color: textColor }}
                                >
                                    {format(activeRole[key])}
                                </span>
                            </div>
                        ))}
                    </div>
                    <TenantLink to="/affiliate/request/payout" className="mt-4">
                        <Button type="primary" block>
                            {t("payout_button")}
                        </Button>
                    </TenantLink>
                    <span className="text-xs text-center mt-2" style={{ color: "#00000040" }}>
                        {t("converted_note")}
                    </span>
                </div>
            }
        >
            <div
                role="button"
                tabIndex={0}
                className="flex items-center gap-2 cursor-pointer text-black mr-2"
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.currentTarget.click();
                    }
                }}
            >
                <DollarOutlined />
                <span className="text-lg">~{format(activeRole.available)}</span>
            </div>
        </Popover>
    );
};
